import Analytics from '@web/services/Analytics/Analytics'
import { APP_ID } from '@web/common/config'
import localforage from 'localforage'
import Appsflyer from '@web/services/Analytics/modules/Appsflyer'
import Logger from '@web/common/Logger'
import { Article } from '@web/types/Article'

export function click (event: Dictionary<string>): void {
  Analytics.send({
    // eslint-disable-next-line camelcase
    element_type: 'other',
    ...event,
    action: 'click'
  })
}

export function openApp (): void {
  Analytics.send({
    action: 'open_app'
  })
}

export function appOpened (typelk: AnalyticsTypeLk): void {
  Analytics.send({
    action: 'app_opened',
    type_lk: typelk
  })
}

export function courseDay ({ type, typelk = 'standart_lk' }: { type: 'start' | 'finish', typelk?: AnalyticsTypeLk }): void {
  const action = `${type}_course_day` as 'start_course_day' | 'finish_course_day'
  Analytics.send({ action, type_lk: typelk })
}

export function login (payload: { method: string; status: 'success' | 'fail'; userId?: number }): void {
  const event: EventBase = {
    action: 'login',
    method: payload.method,
    status: payload.status
  }
  if (payload.userId) {
    // eslint-disable-next-line camelcase
    event.user_id = payload.userId.toString()
  }
  Analytics.send(event)
}

export function registration (payload: { method: string }): void {
  Analytics.send({
    action: 'user_registration',
    method: payload.method
  })
}

export function addDemoCourse (payload: { serviceId: number; courseId: number }): void {
  Analytics.send({
    action: 'demo_course_add',
    // eslint-disable-next-line camelcase
    service_id: payload.serviceId.toString(),
    // eslint-disable-next-line camelcase
    course_id: payload.courseId.toString()
  })
}

export function logout (payload: { userId: number }): void {
  Analytics.send({
    action: 'logout',
    // eslint-disable-next-line camelcase
    user_id: payload.userId.toString()
  })
}

export function rating (payload: {
  type: 'expert' | 'consultation';
  targetId: string;
  userId: string;
  rating: string;
}): void {
  Analytics.send({
    action: `${payload.type}_rating`,
    target_id: payload.targetId,
    user_id: payload.userId,
    rating: payload.rating
  })
}

export function messageSend (typelk: AnalyticsTypeLk = 'standart_lk'): void {
  Analytics.send({ action: 'message_send', type_lk: typelk })
}

export function analyticsChooseHomework ({
  userId,
  courseId,
  courseName,
  homeworkId,
  typelk = 'standart_lk'
}: {
  userId: string;
  courseId: string;
  courseName: string;
  homeworkId: string;
  typelk?: AnalyticsTypeLk;
}): void {
  Analytics.send({
    action: 'choose_homework',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    homework_id: homeworkId,
    type_lk: typelk
  })
}

export function taskSend (payload: {
  homeworkId: string;
  homeworkType: string;
  homeworkStageId?: string;
  homeworkDayId?: string;
  typelk?: AnalyticsTypeLk;
}): void {
  Analytics.send({
    action: 'task_send',
    homework_id: payload.homeworkId,
    homework_type: payload.homeworkType,
    ...(payload.homeworkStageId && { homework_stage_id: payload.homeworkStageId }),
    ...(payload.homeworkDayId && { homework_day_id: payload.homeworkDayId }),
    type_lk: payload.typelk || 'standart_lk'
  })
}

export function filterChange (payload: {
  filterName: string;
  filterValue: string;
  typelk?: AnalyticsTypeLk;
}): void {
  Analytics.send({
    action: 'filter_change',
    filter_name: payload.filterName,
    filter_value: payload.filterValue,
    type_lk: payload.typelk || 'standart_lk'
  })
}

export function firstOpen (): void {
  const KEY = `first_open_${APP_ID}`
  localforage.getItem(KEY)
    .then(isOpen => {
      if (!isOpen) {
        Analytics.send({
          action: 'first_open'
        })
      }
      localforage.setItem(KEY, true)
    })
    .catch(() => {
      Analytics.send({
        action: 'first_open'
      })
    })
}

export function sendChatAnswer (payload: { name: string; type?: string; required?: boolean; pass?: boolean }): void {
  Analytics.send({
    action: 'chat_answer',
    name: payload.name,
    type: payload.type || 'text',
    required: payload.required ? 'true' : 'false',
    pass: payload.pass ? 'true' : 'false'
  })
}

export function firstOpenStage (typelk: AnalyticsTypeLk = 'standart_lk'): void {
  const KEY = `first_open_stage_${APP_ID}`
  localforage.getItem(KEY)
    .then(isOpen => {
      if (!isOpen) {
        Analytics.send({
          action: 'first_open_day',
          type_lk: typelk
        })
      }
      localforage.setItem(KEY, true)
    })
    .catch(() => {
      Analytics.send({
        action: 'first_open_day',
        type_lk: typelk
      })
    })
}

type ParamsCheckout = {
  serviceName: string;
  price: number;
  currency?: string;
  type: string;
  serviceId: number;
  quantity?: number;
}

export function inAppPurchase (): {
  beginCheckout: (paramsCheckout: ParamsCheckout) => void;
  purchase: (paramsCheckout: ParamsCheckout & { orderId: number }) => void;
  } {
  function beginCheckout ({ serviceName, price, currency = '', type, serviceId, quantity = 1 }: ParamsCheckout): void {
    Analytics.send({
      action: 'begin_checkout',
      price: String(price),
      // eslint-disable-next-line camelcase
      item_id: String(serviceId),
      // eslint-disable-next-line camelcase
      currency_code: currency,
      // eslint-disable-next-line camelcase
      payment_info_available: serviceName
    })

    Appsflyer.logEvent('af_initiated_checkout', {
      // eslint-disable-next-line camelcase
      af_content_id: String(serviceId),
      // eslint-disable-next-line camelcase
      af_content_type: type,
      // eslint-disable-next-line camelcase
      af_price: String(price),
      // eslint-disable-next-line camelcase
      af_currency: currency,
      // eslint-disable-next-line camelcase
      af_quantity: String(quantity),
      // eslint-disable-next-line camelcase
      af_payment_info_available: serviceName
    })
  }

  function purchase ({ serviceName, price, currency = '', type, serviceId, quantity = 1, orderId }: ParamsCheckout & { orderId: number }): void {
    Analytics.send({
      action: 'in_app_purchase',
      value: String(price),
      // eslint-disable-next-line camelcase
      item_category: type,
      // eslint-disable-next-line camelcase
      item_id: String(serviceId),
      // eslint-disable-next-line camelcase
      currency_code: currency,
      // eslint-disable-next-line camelcase
      item_name: serviceName,
      // eslint-disable-next-line camelcase
      order_id: String(orderId)
    })

    Appsflyer.logEvent('af_purchase', {
      // eslint-disable-next-line camelcase
      af_revenue: String(price),
      // eslint-disable-next-line camelcase
      af_content_id: String(serviceId),
      // eslint-disable-next-line camelcase
      af_content_type: type,
      // eslint-disable-next-line camelcase
      af_price: String(price),
      // eslint-disable-next-line camelcase
      af_quantity: String(quantity),
      // eslint-disable-next-line camelcase
      af_currency: currency,
      // eslint-disable-next-line camelcase
      af_content: serviceName,
      // eslint-disable-next-line camelcase
      af_order_id: String(orderId)
    })
  }

  return {
    beginCheckout,
    purchase
  }
}

export function videoInDay (): {
  init: (payload: PayloadVideoInDay) => void;
  start: () => void;
  next: () => void;
  autoNext: () => void;
  previous: () => void;
  complete: () => void;
  firstInDay: () => void;
  speed: (speed: number) => void;
  progress: (progress: number) => void;
  } {
  const VIDEO_PROGRESS_LIMITS: number[] = [75, 50, 25, 10]
  let event: InitVideoInDay = {} as InitVideoInDay

  let isVideoFirstInDay = false
  const videoProgresses: Dictionary<Dictionary<boolean>> = {}
  const videoCompleted: Dictionary<boolean> = {}

  return {
    init (payload: PayloadVideoInDay): void {
      event = {
        lesson_title: payload.lessonTitle,
        lesson_id: payload.lessonId,
        video_type: payload.videoType,
        video_orientation: payload.videoOrientation,
        type_lk: payload.typelk,
        day_number: payload.dayNumber.toString(),
        day_lesson_id: payload.lessonId.toString(),
        course_id: payload.courseId.toString(),
        course_name: payload.courseName
      }
    },
    start (): void {
      // TODO (Pominchuk): Пока решили убрать
      // if (!event.lesson_id) {
      //   Logger.log('videoInDay not init. use videoInDay.init(payload: PayloadVideoInDay)')
      //   return
      // }

      // Analytics.send({
      //   ...event,
      //   action: 'video_start'
      // })
    },
    next (): void {
      // TODO (Pominchuk): Пока решили убрать
      // if (!event.lesson_id) {
      //   Logger.log('videoInDay not init. use videoInDay.init(payload: PayloadVideoInDay)')
      //   return
      // }

      // Analytics.send({
      //   ...event,
      //   action: 'play_next'
      // })
    },
    autoNext (): void {
      // TODO (Pominchuk): Пока решили убрать
      // if (!event.lesson_id) {
      //   Logger.log('videoInDay not init. use videoInDay.init(payload: PayloadVideoInDay)')
      //   return
      // }

      // Analytics.send({
      //   ...event,
      //   action: 'auto_play_next'
      // })
    },
    previous (): void {
      // TODO (Pominchuk): Пока решили убрать
      // if (!event.lesson_id) {
      //   Logger.log('videoInDay not init. use videoInDay.init(payload: PayloadVideoInDay)')
      //   return
      // }

      // Analytics.send({
      //   ...event,
      //   action: 'play_previous'
      // })
    },
    complete (): void {
      if (!event.lesson_id) {
        Logger.log('videoInDay not init. use videoInDay.init(payload: PayloadVideoInDay)')
        return
      }

      if (!videoCompleted[event.lesson_id]) {
        Analytics.send({
          ...event,
          action: 'video_complete'
        })
        videoCompleted[event.lesson_id] = true
      }
    },
    firstInDay (): void {
      if (!event.lesson_id) {
        Logger.log('videoInDay not init. use videoInDay.init(payload: PayloadVideoInDay)')
        return
      }

      if (!isVideoFirstInDay) {
        Analytics.send({
          ...event,
          action: 'video_first_in_day'
        })

        isVideoFirstInDay = true
      }
    },
    speed (speed: number): void {
      // TODO (Pominchuk): Пока решили убрать
      // if (!event.lesson_id) {
      //   Logger.log('videoInDay not init. use videoInDay.init(payload: PayloadVideoInDay)')
      //   return
      // }

      // Analytics.send({
      //   ...event,
      //   action: 'video_speed',
      //   speed: speed.toString()
      // })
    },
    progress (progress: number): void {
      if (!event.lesson_id) {
        Logger.log('videoInDay not init. use videoInDay.init(payload: PayloadVideoInDay)')
        return
      }

      const videoKey = `${event.lesson_id}_${event.video_type}_${event.video_orientation}`
      let progressIsSet = false
      VIDEO_PROGRESS_LIMITS.forEach(l => {
        if (!videoProgresses?.[videoKey]?.[l] && l <= progress && !progressIsSet) {
          Analytics.send({
            ...event,
            action: 'video_progress',
            progress: l.toString()
          })

          if (!videoProgresses[videoKey]) {
            videoProgresses[videoKey] = {}
          }
          videoProgresses[videoKey][l] = true
          progressIsSet = true
        }
      })
    }
  }
}

export function courseDayProgress (): (params: { courseDayId: string; courseDayNumber: string; progress: number }) => void {
  const courseDayProgressDays: Dictionary<Dictionary<boolean>> = {}

  return function ({
    courseDayId,
    courseDayNumber,
    progress,
    typelk = 'standart_lk'
  }: { courseDayId: string; courseDayNumber: string; progress: number; typelk?: AnalyticsTypeLk }): void {
    const COURSE_DAY_PROGRESS_LIMIT: number[] = [15, 25, 50, 75, 90]
    const dayKey = `${courseDayId}_${courseDayNumber}`
    COURSE_DAY_PROGRESS_LIMIT.forEach(limit => {
      if (
        !courseDayProgressDays?.[dayKey]?.[limit] &&
        limit <= progress
      ) {
        Analytics.send({ action: 'course_day_progress', progress: limit.toString(), type_lk: typelk })

        if (!courseDayProgressDays[dayKey]) {
          courseDayProgressDays[dayKey] = {}
        }
        courseDayProgressDays[dayKey][limit] = true
      }
    })
  }
}

export function showCommunityInPlatform (typelk: AnalyticsTypeLk = 'standart_lk'): void {
  const key = 'showCommunityInPlatform'
  if (sessionStorage.getItem(key)) {
    return
  }
  sessionStorage.setItem(key, '1')

  Analytics.send({
    action: 'articles_show_lk',
    type_lk: typelk
  })
}

let isScrolledCommunityInPlatform = false
export function scrollCommunityInPlatform (typelk: AnalyticsTypeLk = 'standart_lk'): void {
  if (isScrolledCommunityInPlatform) {
    return
  }
  isScrolledCommunityInPlatform = true

  Analytics.send({
    action: 'articles_preview_scroll',
    type_lk: typelk
  })
}

export function likeArticle ({ source, title, typelk = 'standart_lk' }: { source: string, title: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'like',
    target_type: 'article',
    target_name: title,
    source,
    type_lk: typelk
  })
}

export function dislikeArticle ({ source, title, typelk = 'standart_lk' }: { source: string, title: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'dislike',
    target_type: 'article',
    target_name: title,
    source,
    type_lk: typelk
  })
}

export function favouriteArticle ({ source, title, typelk = 'standart_lk' }: { source: string, title: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'favourite',
    target_type: 'article',
    target_name: title,
    source,
    type_lk: typelk
  })
}

export function unfavouriteArticle ({ source, title, typelk = 'standart_lk' }: { source: string, title: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'unfavourite',
    target_type: 'article',
    target_name: title,
    source,
    type_lk: typelk
  })
}

const sentBannerShowKeys = new Set<string>()
const sentArticlesPreviewKeys = new Set<string>()

export function analyticsArticlesPreview ({ article }: { article?: Article }): void {
  if (!article) return
  const key = article.id.toString()
  if (sentArticlesPreviewKeys.has(key)) return
  sentArticlesPreviewKeys.add(key)
  Analytics.send({
    action: 'articles_preview',
    article_id: article.id.toString(),
    article_title: article.title
  })
}

// A/B-тест баннера (MOB-606): id из API, вне теста — пустая строка, как остальные необязательные поля баннера
function bannerAbTestParams (banner: Banner) {
  return {
    banner_ab_test_id: banner.abTestId != null ? String(banner.abTestId) : '',
    banner_ab_test_variant_id: banner.variationId != null ? String(banner.variationId) : ''
  }
}

export function analyticsBannerShow ({ banner, position, url, crossSegments = [], typelk = 'standart_lk' }: { banner: Banner, position: string, url: string, crossSegments: Segment[], typelk?: AnalyticsTypeLk }): void {
  const key = `${banner.id}::${position}`
  if (sentBannerShowKeys.has(key)) return
  sentBannerShowKeys.add(key)
  Analytics.send({
    action: 'banner_show',
    target_type: 'banner',
    target_name: banner.title,
    target_id: banner.id.toString(),
    target_position: position,
    target_url: url,
    banner_title: banner.title,
    banner_id: banner.id.toString(),
    banner_position: position,
    banner_url: url,
    type: banner.type || '',
    banner_type: banner.type || '',
    analytic_product_name: banner.analyticProductName || '',
    analytic_school_title: banner.analyticSchoolTitle || '',
    banner_segments: banner.segments.map(segment => segment.name).join(', '),
    ...bannerAbTestParams(banner),
    cross_segments: crossSegments.map(segment => segment.name).join(', '),
    type_lk: typelk
  })
}
export function analyticsBannerClick ({ banner, position, url, crossSegments = [], typelk = 'standart_lk' }: { banner: Banner, position: string, url: string, crossSegments: Segment[], typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'banner_click',
    target_type: 'banner',
    target_name: banner.title,
    target_id: banner.id.toString(),
    target_position: position,
    target_url: url,
    banner_title: banner.title,
    banner_id: banner.id.toString(),
    banner_position: position,
    banner_url: url,
    type: banner.type || '',
    banner_type: banner.type || '',
    analytic_product_name: banner.analyticProductName || '',
    analytic_school_title: banner.analyticSchoolTitle || '',
    banner_segments: banner.segments.map(segment => segment.name).join(', '),
    ...bannerAbTestParams(banner),
    cross_segments: crossSegments.map(segment => segment.name).join(', '),
    type_lk: typelk
  })
}
export function analyticsLkFirstVisit (projectId: 'any' | 'take'): void {
  Analytics.send({
    action: 'lk_first_visit',
    event_date: new Date().toISOString(),
    project_id: projectId
  })
}
